/**
 * `GET /api/activity` and `GET /api/activity/:id` (slice 2c spec §7). Every
 * key starts `['activity']`: a `turn` hub event invalidates them all (§7.3),
 * so a running turn's row and its receipt follow it.
 */
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { api } from "../../app/api";
import type { AreaRef } from "../areas/api";
import { searchFor, type Filters, type StartedByKind, type Used } from "./filters";

export const activityKey = ["activity"] as const;

type AgentRef = { id: string; slug: string; name: string; glyph: string; tint: string };

export type ActivityItem = {
  id: string;
  type: "turn" | "unexpected";
  at: string;
  agent: AgentRef | null;
  title: string;
  thread_id: string | null;
  started_by: {
    kind: StartedByKind;
    label: string | null;
    /** The task a New task, follow-through or nudge was about (slice 2b). */
    task?: { id: string; title: string } | null;
    /** The doc a person's message was about (slice 2d): "About <title>". */
    doc?: { id: string; title: string } | null;
    /** The app a person's text came by (slice 2e; email, slice 2h): its `label` is "<name> via iMessage". */
    via?: "imessage" | "email" | null;
    /** Who a task's or routine's turn is for (slice 6a), a first name; null for a person's. */
    for?: string | null;
  };
  status: "queued" | "running" | "done" | "failed" | "unexpected";
  fail_reason: string | null;
  can_retry: boolean;
  progress: { text: string } | null;
  steps: Record<string, number>;
  /** Steps Activity doesn't show (commands): a count only. */
  steps_hidden: number;
  fallback: { model: string; label: string } | null;
  tokens: number | null;
  computer_ms: number | null;
  /** Its thread's Area (slice 6d spec §4.4), null with none. Missing on an older Worker. */
  area?: AreaRef | null;
};

export type ActivityDay = { date: string; turns: number; computer_ms: number; tokens: number };

export type ActivityPage = {
  items: ActivityItem[];
  days: ActivityDay[];
  counts: {
    status: { all: number; working: number; failed: number; unexpected: number };
    agent: Record<string, number>;
    started_by: Partial<Record<StartedByKind, number>>;
    used: Record<Used, number>;
  };
  totals: { computer_ms: number; tokens: number; tokens_cache_read: number };
  next_cursor: string | null;
};

export type ReceiptStep = {
  seq: number;
  call_id: string;
  tool: string;
  kind: string;
  status: "ok" | "error" | "unknown";
  error: string | null;
  params: string | null;
  result: string | null;
  details: string | null;
  duration_ms: number | null;
  units: number | null;
  detail: string;
  /** "Sam via Maya's Gmail": an app call with someone else's shared account (slice 6a §5.2). */
  via?: string;
};

export type Receipt = Omit<ActivityItem, "steps"> & {
  started_at: string | null;
  finished_at: string | null;
  said: string | null;
  touched: (
    | { kind: "memory_file"; name: string; file_id: string; version: number | null }
    | { kind: "task"; task_id: string; name: string }
    | { kind: "doc"; doc_id: string; title: string }
  )[];
  steps: ReceiptStep[];
  steps_dropped: number;
  model: string | null;
  model_label: string | null;
  account: string | null;
  routed_from: string | null;
  tokens_in: number | null;
  tokens_out: number | null;
  tokens_cache_read: number | null;
  web_today: { used: number; limit: number };
  recovered: boolean;
  technical: {
    turn_id: string | null;
    run_id: string | null;
    session_key: string | null;
    lease_gen: number | null;
  };
};

/** The list, 50 a page; `fetchNextPage` loads the next. */
export function useActivity(filters: Filters) {
  const search = searchFor(filters).toString();
  return useInfiniteQuery({
    queryKey: [...activityKey, "list", search],
    queryFn: ({ pageParam }) => {
      const params = new URLSearchParams(search);
      if (pageParam) params.set("cursor", pageParam);
      const query = params.toString();
      return api<ActivityPage>("GET", `/api/activity${query ? `?${query}` : ""}`);
    },
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.next_cursor,
  });
}

export function useReceipt(id: string | undefined) {
  return useQuery({
    queryKey: [...activityKey, "receipt", id],
    queryFn: () =>
      api<{ receipt: Receipt }>("GET", `/api/activity/${encodeURIComponent(id ?? "")}`).then(
        (r) => r.receipt,
      ),
    enabled: Boolean(id),
  });
}
